import { useState, useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, ChevronRight } from 'lucide-react';
import usePromise from '@/hooks/usePromise';
import { entityConfigs, Customer } from '@/lib/entities/entityConfig';
import { Project, Work } from '@certhon/models';
import { FieldComponentProps } from './types';
import { useScrollIntoViewOnFocus } from '@/hooks/useScrollIntoViewOnFocus';

// Configuration for which levels are allowed for a specific entity
export type CascadingConstraints = {
  allowCustomer?: boolean;
  allowProject?: boolean;
  allowWork?: boolean;
};

// Internal state to track the full cascading path
interface CascadingState {
  customerId: number | null;
  projectId: number | null;
  workId: number | null;
}

// View component for displaying the cascading path
function CascadingView({
  customerId,
  projectId,
  workId,
}: {
  customerId: number | null;
  projectId: number | null;
  workId: number | null;
}) {
  const customer = usePromise(
    useMemo(
      () =>
        customerId
          ? entityConfigs.customer.getById(String(customerId))
          : Promise.resolve(null),
      [customerId],
    ),
  );

  const project = usePromise(
    useMemo(
      () =>
        projectId
          ? entityConfigs.project.getById(String(projectId))
          : Promise.resolve(null),
      [projectId],
    ),
  );

  const work = usePromise(
    useMemo(
      () =>
        workId
          ? entityConfigs.work.getById(String(workId))
          : Promise.resolve(null),
      [workId],
    ),
  );

  const isLoading =
    (customerId && !customer) || (projectId && !project) || (workId && !work);

  if (isLoading) {
    return <Skeleton className="h-4 w-64" />;
  }

  if (!customer && !project && !work) {
    return <p className="text-muted-foreground">No selection</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {customer && (
        <>
          {entityConfigs.customer.renderBadge(customer)}
          {(project || work) && (
            <ChevronRight className="text-muted-foreground h-4 w-4" />
          )}
        </>
      )}
      {project && (
        <>
          {entityConfigs.project.renderBadge(project)}
          {work && <ChevronRight className="text-muted-foreground h-4 w-4" />}
        </>
      )}
      {work && entityConfigs.work.renderBadge(work)}
    </div>
  );
}

// Single level combobox component
function LevelCombobox<T>({
  label,
  value,
  options,
  isLoading,
  disabled,
  onSelect,
  renderBadge,
  getOptionValue,
  getOptionLabel,
  isActive,
  isInvalid,
}: {
  label: string;
  value: number | null;
  options: T[];
  isLoading: boolean;
  disabled: boolean;
  onSelect: (value: number | null) => void;
  renderBadge: (entity: T) => React.ReactElement;
  getOptionValue: (entity: T) => number;
  getOptionLabel: (entity: T) => string;
  isActive?: boolean;
  isInvalid?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((opt) => getOptionValue(opt) === value);

  const handleSelect = (newValue: number | null) => {
    onSelect(newValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between',
            !selectedOption && 'text-muted-foreground',
            disabled && 'bg-muted cursor-not-allowed opacity-50',
            isActive && !isInvalid && 'border-3',
            isInvalid && 'border-3 border-red-500',
          )}
        >
          {selectedOption ? renderBadge(selectedOption) : `Select ${label}`}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder={`Search ${label}...`} className="h-9" />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading...' : `No ${label} found.`}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value="none" onSelect={() => handleSelect(null)}>
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === null ? 'opacity-100' : 'opacity-0',
                  )}
                />
                No {label}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={getOptionValue(option)}
                  value={String(getOptionValue(option))}
                  keywords={[getOptionLabel(option)]}
                  onSelect={() => handleSelect(getOptionValue(option))}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === getOptionValue(option)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  {renderBadge(option)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Edit component with cascading comboboxes
function CascadingEdit({
  value,
  onChange,
  constraints,
}: {
  value: CascadingState;
  onChange: (state: CascadingState) => void;
  constraints: CascadingConstraints;
}) {
  const fieldRef = useScrollIntoViewOnFocus<HTMLDivElement>({ bufferPx: 400 });
  const [state, setState] = useState<CascadingState>(value);

  // Sync external value changes to internal state
  // BUT preserve the customer selection since it's only for filtering
  useEffect(() => {
    setState((prev) => ({
      ...value,
      customerId: value.customerId || prev.customerId, // Keep customer if external value doesn't have one
    }));
  }, [value]);

  // Fetch customers
  const customers = usePromise(
    useMemo(() => entityConfigs.customer.search(), []),
  ) as Customer[] | undefined;

  // Fetch projects filtered by customer (or all if no customer selected)
  const projects = usePromise(
    useMemo(
      () => entityConfigs.project.search('', { customerId: state.customerId }),
      [state.customerId],
    ),
  ) as Project[] | undefined;

  // Fetch works filtered by project
  const works = usePromise(
    useMemo(
      () =>
        state.projectId
          ? entityConfigs.work.search('', { projectId: state.projectId })
          : Promise.resolve([]),
      [state.projectId],
    ),
  ) as Work[] | undefined;

  const handleCustomerChange = (customerId: number | null) => {
    const newState: CascadingState = {
      customerId,
      projectId: null,
      workId: null,
    };
    setState(newState);
    onChange(newState);
  };

  const handleProjectChange = (projectId: number | null) => {
    // Selecting a project pins the customer to the project's klant (CRM
    // chain); clearing it keeps the customer for filtering purposes.
    const project = projectId
      ? projects?.find((p) => p.recnum === projectId)
      : undefined;
    const newState: CascadingState = {
      customerId: project?.klant_id ?? state.customerId,
      projectId,
      workId: null,
    };
    setState(newState);
    onChange(newState);
  };

  const handleWorkChange = (workId: number | null) => {
    // Selecting a work pins the full chain to the work's own CRM relations
    // (most specific node wins — a werk carries its own klant).
    const work = workId ? works?.find((w) => w.recnum === workId) : undefined;
    const newState: CascadingState = {
      customerId: work?.klant_id ?? state.customerId,
      projectId: work
        ? work.project_id != null
          ? Number(work.project_id)
          : null
        : state.projectId,
      workId,
    };
    setState(newState);
    onChange(newState);
  };

  // Determine which levels are visible
  const showCustomer = true; // Always show customer for context/filtering
  const showProject = constraints.allowProject !== false;
  const showWork = constraints.allowWork !== false;

  // Determine which is the active (determining) level - the most specific one with a value
  const activeLevel = state.workId
    ? 'work'
    : state.projectId
      ? 'project'
      : state.customerId
        ? 'customer'
        : null;

  // Check if current selection is invalid (customer selected but not allowed as final selection)
  const isInvalidSelection =
    state.customerId !== null &&
    state.projectId === null &&
    state.workId === null &&
    constraints.allowCustomer === false;

  return (
    <div
      ref={fieldRef}
      className="flex w-full flex-col gap-1 @xl:flex-row @2xl:items-center @2xl:gap-2"
    >
      {showCustomer && (
        <div className="flex items-center gap-2 @xl:flex-1">
          <span className="text-muted-foreground w-24 shrink-0 text-xs @xl:hidden">
            Customer
          </span>
          <div className="flex-1">
            <LevelCombobox<Customer>
              label="Customer"
              value={state.customerId}
              options={customers || []}
              isLoading={!customers}
              disabled={false}
              onSelect={handleCustomerChange}
              renderBadge={(c) => entityConfigs.customer.renderBadge(c)}
              getOptionValue={(c) =>
                entityConfigs.customer.getOptionValue(c) as number
              }
              getOptionLabel={(c) => entityConfigs.customer.getOptionLabel(c)}
              isActive={activeLevel === 'customer' && !isInvalidSelection}
              isInvalid={isInvalidSelection}
            />
          </div>
        </div>
      )}

      {showProject && (
        <div className="flex items-center gap-2 @xl:flex-1">
          <span className="text-muted-foreground w-24 shrink-0 text-xs @xl:hidden">
            Project
          </span>
          <div className="flex-1">
            <LevelCombobox<Project>
              label="Project"
              value={state.projectId}
              options={projects || []}
              isLoading={!projects}
              disabled={false}
              onSelect={handleProjectChange}
              renderBadge={(p) => entityConfigs.project.renderBadge(p)}
              getOptionValue={(p) =>
                entityConfigs.project.getOptionValue(p) as number
              }
              getOptionLabel={(p) => entityConfigs.project.getOptionLabel(p)}
              isActive={activeLevel === 'project'}
            />
          </div>
        </div>
      )}

      {showWork && (
        <div className="flex items-center gap-2 @xl:flex-1">
          <span className="text-muted-foreground w-24 shrink-0 text-xs @xl:hidden">
            Work
          </span>
          <div className="flex-1">
            <LevelCombobox<Work>
              label="Work"
              value={state.workId}
              options={works || []}
              isLoading={state.projectId !== null && !works}
              disabled={state.projectId === null}
              onSelect={handleWorkChange}
              renderBadge={(w) => entityConfigs.work.renderBadge(w)}
              getOptionValue={(w) =>
                entityConfigs.work.getOptionValue(w) as number
              }
              getOptionLabel={(w) => entityConfigs.work.getOptionLabel(w)}
              isActive={activeLevel === 'work'}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Derive cascading state from raw form values (which only stores the most specific ID)
async function deriveStateFromFormValues(
  projectId: number | null,
  workId: number | null,
): Promise<CascadingState> {
  try {
    // If work is set, derive project and customer from work
    if (workId) {
      const work = await entityConfigs.work.getById(String(workId));
      return {
        customerId: work.klant_id,
        // a werk can be project-less
        projectId: work.project_id != null ? Number(work.project_id) : null,
        workId: work.recnum,
      };
    }

    // If project is set, derive customer from project
    if (projectId) {
      const project = await entityConfigs.project.getById(String(projectId));
      return {
        customerId: project.klant_id,
        projectId: project.recnum,
        workId: null,
      };
    }

    // No selection
    return {
      customerId: null,
      projectId: null,
      workId: null,
    };
  } catch (error) {
    console.error('Failed to derive cascading state:', error);
    // Return empty state if derivation fails
    return {
      customerId: null,
      projectId: null,
      workId: null,
    };
  }
}

export function CascadingProjectReference({
  formMode,
  field,
}: FieldComponentProps & {
  field: { meta: { constraints?: CascadingConstraints } };
}) {
  const constraints: CascadingConstraints = field.meta.constraints || {
    allowProject: true,
    allowWork: true,
  };

  // Access the form instance to read/write both project_id and werk_id
  const form = useFormContext();
  const currentProjectId = form.watch('project_id') ?? null;
  const currentWerkId = form.watch('werk_id') ?? null;

  // Derive the full cascading state from current form values
  const cascadingState = usePromise(
    useMemo(
      () => deriveStateFromFormValues(currentProjectId, currentWerkId),
      [currentProjectId, currentWerkId],
    ),
  );

  const handleChange = (newState: CascadingState) => {
    // CRM-chain contract: write the FULL ancestor chain resolved from the
    // most specific selected node — a werk-level selection carries its
    // werk's project and klant as explicit values, a project-level
    // selection carries the project's klant. client_id is maintained here
    // so submit handlers can send the chain without re-resolving it.
    // Use shouldDirty and shouldTouch to properly trigger form state updates
    const setValueOptions = {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    };

    if (newState.workId) {
      form.setValue('werk_id', newState.workId, setValueOptions);
      form.setValue('project_id', newState.projectId ?? null, setValueOptions);
      form.setValue('client_id', newState.customerId ?? null, setValueOptions);
    } else if (newState.projectId) {
      form.setValue('project_id', newState.projectId, setValueOptions);
      form.setValue('werk_id', null, setValueOptions);
      form.setValue('client_id', newState.customerId ?? null, setValueOptions);
    } else if (newState.customerId && constraints.allowCustomer) {
      // klant-level reference — only for entities whose policy allows it
      form.setValue('project_id', null, setValueOptions);
      form.setValue('werk_id', null, setValueOptions);
      form.setValue('client_id', newState.customerId, setValueOptions);
    } else {
      form.setValue('project_id', null, setValueOptions);
      form.setValue('werk_id', null, setValueOptions);
      form.setValue('client_id', null, setValueOptions);
    }
  };

  if (formMode === 'loading' || !cascadingState) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (formMode === 'edit') {
    return (
      <CascadingEdit
        value={cascadingState}
        onChange={handleChange}
        constraints={constraints}
      />
    );
  }

  return (
    <CascadingView
      customerId={cascadingState.customerId}
      projectId={cascadingState.projectId}
      workId={cascadingState.workId}
    />
  );
}
